import uniq from "lodash/uniq";

import { Dinero, dineroCADZero, multiplyScaled } from "@/utils/dinero-util";
import { TaxBreakdown, TaxRateScale, TaxCode } from "./tax-breakdown";
import { add, allocate, subtract } from "dinero.js";
import { VendorWithPricingInformation } from "./vendor";

export type ConstructorOptions =
  | { errors: string[] }
  | { subtotal: Dinero; taxCode: TaxCode }
  | { total: Dinero; taxCode: TaxCode }
  | { subtotal: Dinero; taxes: TaxBreakdown };

export type ForItemAndVendorConstructorOptions =
  | { item: { name: string; purchaseTaxCode: TaxCode | null }; vendor: VendorWithPricingInformation; subtotal: Dinero }
  | { item: { name: string; purchaseTaxCode: TaxCode | null }; vendor: VendorWithPricingInformation; total: Dinero };

/**
 * This class represents the breakdown of a cost into a subtotal and taxes, with tracking for the amount of each tax code applied.
 */
export class CostTaxBreakdown {
  /** Static instance for a cost of $0.00 with no taxed applied */
  public static ZERO = new CostTaxBreakdown({ subtotal: dineroCADZero, taxes: TaxBreakdown.EMPTY });

  /** Create a new cost breakdown with the specified error. */
  public static withError(error: string): CostTaxBreakdown {
    return new CostTaxBreakdown({ errors: [error] });
  }

  /**
   * Create a cost breakdown for the specified item and vendor. The tax code from the item is used if it exists, otherwise
   * the tax code from the vendor is used. If neither has a tax code, an error is returned.
   *
   * In general, the item's tax code should match the vendor's tax code, but there are cases where they may differ (e.g. a
   * vendor that sells both taxable and non-taxable items, or the labour items (exempt) that are under the Senti vendor (HST)).
   */
  public static forItemAndVendor({ item, vendor, ...options }: ForItemAndVendorConstructorOptions): CostTaxBreakdown {
    const taxCode = item.purchaseTaxCode || vendor.taxCode;
    if (!taxCode)
      return CostTaxBreakdown.withError(
        `Item ${item.name} has no purchase tax code selected, and neither does it's preferred vendor '${vendor.name}'`,
      );
    return new CostTaxBreakdown({ ...options, taxCode });
  }

  public readonly subtotal: Dinero;
  public readonly taxes: TaxBreakdown;
  public readonly tax: Dinero;
  public readonly total: Dinero;

  /**
   * Any errors that occurred while creating the cost breakdown. If this array is empty, the cost breakdown is valid.
   * If this array has one or more elements, the cost breakdown is invalid and the cost values should not be used.
   */
  public readonly errors: string[];

  constructor(options: ConstructorOptions) {
    if ("errors" in options) {
      this.errors = options.errors;
      this.subtotal = dineroCADZero;
      this.taxes = TaxBreakdown.EMPTY;
      this.total = dineroCADZero;
      this.tax = dineroCADZero;
      return;
    } else {
      this.errors = [];
    }

    if ("taxes" in options) {
      this.subtotal = options.subtotal;
      this.taxes = options.taxes;
      this.total = add(options.subtotal, options.taxes.total);
    } else {
      const taxCode = options.taxCode!;
      const taxRate = taxCode.purchaseTaxRate / 100;

      if ("total" in options) {
        this.total = options.total;
        this.subtotal = multiplyScaled(this.total, { amount: 1 / (1 + taxRate), scale: TaxRateScale });
        const tax = subtract(this.total, this.subtotal);
        this.taxes = new TaxBreakdown(new Map([[taxCode, tax]]));
      } else {
        this.subtotal = options.subtotal;
        const tax = multiplyScaled(options.subtotal, { amount: taxRate, scale: TaxRateScale });
        this.total = add(this.subtotal, tax);
        this.taxes = new TaxBreakdown(new Map([[taxCode, tax]]));
      }
    }

    this.tax = this.taxes.total;
  }

  public add(other: CostTaxBreakdown): CostTaxBreakdown {
    const errors = uniq(this.errors.concat(other.errors));
    if (errors.length) return new CostTaxBreakdown({ errors });

    return new CostTaxBreakdown({
      subtotal: add(this.subtotal, other.subtotal),
      taxes: this.taxes.add(other.taxes),
    });
  }

  public subtract(other: CostTaxBreakdown): CostTaxBreakdown {
    const errors = uniq(this.errors.concat(other.errors));
    if (errors.length) return new CostTaxBreakdown({ errors });

    return new CostTaxBreakdown({
      subtotal: subtract(this.subtotal, other.subtotal),
      taxes: this.taxes.subtract(other.taxes),
    });
  }

  /**
   * See: https://v2.dinerojs.com/docs/api/mutations/multiply
   */
  public multiply(amount: number, scale = 2): CostTaxBreakdown {
    if (this.errors.length) return this;

    return new CostTaxBreakdown({
      subtotal: multiplyScaled(this.subtotal, { amount, scale }),
      taxes: this.taxes.multiply(amount),
    });
  }

  /**
   * See: https://v2.dinerojs.com/docs/api/mutations/allocate
   */
  public allocate(ratios: number[]): CostTaxBreakdown[] {
    if (this.errors.length) return ratios.map(() => this);

    const allocatedSubtotals = allocate(this.subtotal, ratios);
    const allocatedTaxes = this.taxes.allocate(ratios);
    return allocatedSubtotals.map(
      (subtotal, i) =>
        new CostTaxBreakdown({
          subtotal,
          taxes: allocatedTaxes[i]!,
        }),
    );
  }
}
